import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {AvatarColorVariants} from '#lit/components/base/avatar/index.js';
import {isNonEmptyString} from '#lit/utils/index.js';

import styles from './style.scss';

export const COMPONENT_TAG = 'fly-user-avatar';

export const DEFAULT_SIZE = '30px';

export enum Shape {
  CIRCLE = 'circle',
  SQUARE = 'square',
  ROUNDED = 'rounded',
}

@customElement(COMPONENT_TAG)
export default class FlyUserAvatar extends FlywheelComponent {
  @property({attribute: 'has-tooltip', type: Boolean})
  tooltip = false;

  @property({attribute: 'has-menu', type: Boolean})
  menu = false;

  @property({type: String})
  image = '';

  @property({type: String})
  alt = 'User avatar image';

  @property({type: String})
  size: string = DEFAULT_SIZE;

  @property({type: String})
  theme: AvatarColorVariants = AvatarColorVariants.SECONDARY;

  @property({type: String})
  initials = '';

  @property({type: String})
  shape: Shape = Shape.CIRCLE;

  @property({type: String})
  link = '';

  /** DOM nodes registry for icon, tooltip, menu, and possibly more...  */
  private $nodes: Map<string, HTMLElement> = new Map<string, HTMLElement>();

  static get styles() {
    return [styles];
  }

  load() {
    if (isNonEmptyString(this.image)) {
      this.initials = '';
    }

    if (this.menu) {
      this.tooltip = false;
    }
  }

  registerTooltip() {}

  registerPopover() {}

  registerIcon() {}

  menuMarkup() {
    if (!this.menu) return html``;
    return html`
      <div class="fly-user-avatar__menu">
        <slot name="menu" @slotchange=${this.registerPopover}></slot>
      </div>
    `;
  }

  tooltipMarkup() {
    if (!this.tooltip || this.menu) return html``;
    return html`
      <div class="fly-user-avatar__tooltip">
        <slot name="tooltip" @slotchange=${this.registerTooltip}></slot>
      </div>
    `;
  }

  iconMarkup() {
    if (isNonEmptyString(this.initials) || isNonEmptyString(this.image)) {
      return html``;
    }

    return html`
      <div class="fly-user-avatar__icon">
        <slot name="icon" @slotchange=${this.registerIcon}></slot>
      </div>
    `;
  }

  imageMarkup() {
    if (isNonEmptyString(this.initials) || !isNonEmptyString(this.image)) {
      return html``;
    }

    return html`
      <img class="fly-user-avatar__image" src=${this.image} alt=${this.alt} />
    `;
  }

  initialsMarkup() {
    if (!isNonEmptyString(this.initials) || isNonEmptyString(this.image)) {
      return html``;
    }

    return html`
      <div class="fly-user-avatar__initials">${this.initials}</div>
    `;
  }

  get classes(): Record<string, ClassInfo> {
    return {
      container: {
        'fly-user-avatar-container': true,
      },
      main: {
        'fly-user-avatar-main': true,
      },
      aside: {
        'fly-user-avatar-aside': true,
      },
      avatar: {
        'fly-user-avatar': true,
        [`fly-user-avatar--${this.shape}`]: true,
        [`fly-user-avatar--${this.theme}`]: true,
        'fly-user-avatar--has-tooltip': this.tooltip,
        'fly-user-avatar--has-menu': this.menu,
        'fly-user-avatar--link': isNonEmptyString(this.link),
      },
    };
  }

  callback() {}

  render() {
    this.style.setProperty('--fly-user-avatar-size', this.size);
    return html`
      <div class=${classMap(this.classes.container)}>
        <main class=${classMap(this.classes.main)}>
          <div
            class=${classMap(this.classes.avatar)}
            @click="${(_: MouseEvent) => {
              if (isNonEmptyString(this.link)) {
                window.location.href = this.link;
                return;
              }
              this.callback();
            }}"
          >
            ${this.initialsMarkup()}
            <!---->
            ${this.imageMarkup()}
            <!---->
            ${this.iconMarkup()}
          </div>
          ${this.tooltipMarkup()}
          <!---->
          ${this.menuMarkup()}
        </main>
        <aside class=${classMap(this.classes.aside)}>
          <slot></slot>
        </aside>
      </div>
    `;
  }

  static initialsFrom(input: unknown) {
    if (!isNonEmptyString(input)) return '-';
    /** todo: probably needs to be updated later on to accomodate last name & first name initials */
    return input.substring(0, 1);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyUserAvatar;
  }
}
